/**
 * ============================================================
 * 全局主题配置文件
 * ------------------------------------------------------------
 * 修改此处变量即可全局改变界面风格（需在入口文件调用 applyTheme()）。
 * 支持用户在页面中自定义部分项（saveThemeConfig / localStorage 持久化）。
 *
 * 使用方式（全局样式 global.css / element-override.css / 业务页面）：
 *   color: var(--color-primary);
 *   font-size: var(--font-base);
 *   background: var(--menu-bg);
 * ============================================================
 */

// ---- 默认主题配置 ----
const theme = {
  // ---------- 颜色 ----------
  primary: '#1A7BFC',          // 主色（普通按钮/激活态）
  primaryHover: '#4A9BFD',     // 主色 hover
  primaryActive: '#0F66D9',    // 主色 active

  success: '#67C23A',          // 成功
  successHover: '#85CE61',
  warning: '#E6A23C',          // 警告
  warningHover: '#EBB563',

  // 危险色 = 删除类按钮的全局统一颜色（所有删除/批量删除按钮均使用此色，可统一配置）
  danger: '#F56C6C',
  dangerHover: '#F78989',
  dangerActive: '#E25C5C',

  info: '#909399',             // 中性
  infoHover: '#A6A9AD',

  // 特殊按钮语义色（保持原项目风格，可单独配置）
  goon: '#806BFF',             // 原 goon 紫 rgba(128,107,255)
  goonHover: 'rgba(128, 107, 255, 0.8)',
  examine: '#11E2A3',          // 原 examine 绿 rgba(17,226,163)
  examineHover: 'rgba(17, 226, 163, 0.8)',

  // ---------- 业务辅助色（7b 抽取，页面可直接引用） ----------
  infoBlue: '#57bcf5',          // 浅蓝（统计数字/搜索计数提示）
  successBright: '#0dd100',     // 亮绿（培训/实习活动强调按钮）
  deepBlue: '#009dd9',          // 深蓝（轮转进度条/圆点）
  unobserved: '#85b4ff',        // 淡蓝紫（评教"未观察"标记）
  labBlue: '#3499db',           // 中蓝（实验室选中态）
  cyanLight: '#ccffff',         // 浅青（实验室表格选中背景）
  orange: '#ff9b00',            // 橙色（实验室强调 hover）

  // ---------- 侧边栏（logo区 + 菜单区统一深色）----------
  sidebarBg: '#164E80',           // 侧边栏背景（logo区 + 菜单区）
  sidebarText: '#E8F1FB',         // 侧边栏菜单文字
  sidebarActiveText: '#FFFFFF',   // 侧边栏激活文字
  sidebarHoverBg: '#2265A1',      // 侧边栏 hover 背景
  sidebarActiveBg: '#1A7BFC',     // 侧边栏激活背景

  // ---------- 登录页（步骤 14：与侧边栏/主色/信息蓝同族协调，变量化收口）----------
  loginBgDeep: '#0B2A5B',           // 登录页背景渐变起点（深海蓝，比侧边栏深一档）

  // ---------- 主色衍生（步骤 14：浅色底/光晕/投影统一收口，改主色时建议同步调整）----------
  primarySoft: 'rgba(26, 123, 252, 0.02)',       // 极浅底（表格斑马纹）
  primarySoftLight: 'rgba(26, 123, 252, 0.04)',  // 表格行 hover
  primarySoftBase: 'rgba(26, 123, 252, 0.06)',   // 表头 / 默认按钮 hover 底
  primarySoftStrong: 'rgba(26, 123, 252, 0.08)', // 表格当前行 / 菜单 hover（= menuHoverBg）
  primaryGlow: 'rgba(26, 123, 252, 0.15)',       // 输入框聚焦光晕
  primaryShadow: 'rgba(26, 123, 252, 0.35)',     // 主按钮投影
  primaryShadowHover: 'rgba(26, 123, 252, 0.45)', // 主按钮 hover 投影

  // ---------- 框架配色 ----------
  headerBg: '#FFFFFF',         // 顶栏背景
  pageBg: '#F6FAFE',           // 内容区背景
  cardBg: '#FFFFFF',           // 卡片背景
  menuBg: '#FFFFFF',           // 左侧菜单背景
  menuText: '#666666',         // 菜单文字
  menuActiveText: '#1A7BFC',   // 菜单激活文字
  menuHoverBg: 'rgba(26, 123, 252, 0.08)', // 菜单 hover 背景
  menuActiveBg: 'rgba(26, 123, 252, 0.12)', // 菜单激活背景

  // ---------- 布局 ----------
  asideWidth: '200px',          // 左侧菜单栏宽度（自适应缩放以 1920 为基准，见 plugins/adaptive.js）

  // ---------- 组件 ----------
  tableHeaderBg: 'rgba(26, 123, 252, 0.06)', // 表格表头背景

  // ---------- 文本 ----------
  textPrimary: '#303133',
  textRegular: '#606266',
  textSecondary: '#909399',
  borderColor: '#E4E7ED',

  // ---------- 字号 ----------
  fontXs: '12px',
  fontSm: '13px',
  fontBase: '14px',
  fontMd: '16px',
  fontLg: '18px',

  // ---------- 间距 ----------
  spaceXs: '4px',
  spaceSm: '8px',
  spaceBase: '12px',
  spaceLg: '16px',
  spaceXl: '24px',

  // ---------- 圆角 ----------
  radiusSm: '2px',
  radiusBase: '4px',
  radiusLg: '8px',

  // ---------- 阴影 ----------
  shadowCard: '0 1px 4px rgba(0, 0, 0, 0.08)',
  shadowPop: '0 2px 12px rgba(0, 0, 0, 0.10)',
}

// 配置项 -> CSS 变量名 映射表
const varMap = {
  primary: '--color-primary',
  primaryHover: '--color-primary-hover',
  primaryActive: '--color-primary-active',
  success: '--color-success',
  successHover: '--color-success-hover',
  warning: '--color-warning',
  warningHover: '--color-warning-hover',
  danger: '--color-danger',
  dangerHover: '--color-danger-hover',
  dangerActive: '--color-danger-active',
  info: '--color-info',
  infoHover: '--color-info-hover',
  goon: '--color-goon',
  goonHover: '--color-goon-hover',
  examine: '--color-examine',
  examineHover: '--color-examine-hover',
  infoBlue: '--color-info-blue',
  successBright: '--color-success-bright',
  deepBlue: '--color-deep-blue',
  unobserved: '--color-unobserved',
  labBlue: '--color-lab-blue',
  cyanLight: '--color-cyan-light',
  orange: '--color-orange',
  loginBgDeep: '--color-login-bg-deep',
  primarySoft: '--color-primary-soft',
  primarySoftLight: '--color-primary-soft-light',
  primarySoftBase: '--color-primary-soft-base',
  primarySoftStrong: '--color-primary-soft-strong',
  primaryGlow: '--color-primary-glow',
  primaryShadow: '--color-primary-shadow',
  primaryShadowHover: '--color-primary-shadow-hover',
  sidebarBg: '--sidebar-bg',
  sidebarText: '--sidebar-text',
  sidebarActiveText: '--sidebar-active-text',
  sidebarHoverBg: '--sidebar-hover-bg',
  sidebarActiveBg: '--sidebar-active-bg',
  headerBg: '--header-bg',
  pageBg: '--page-bg',
  cardBg: '--card-bg',
  menuBg: '--menu-bg',
  menuText: '--menu-text',
  menuActiveText: '--menu-active-text',
  menuHoverBg: '--menu-hover-bg',
  menuActiveBg: '--menu-active-bg',
  tableHeaderBg: '--table-header-bg',
  asideWidth: '--aside-width',
  textPrimary: '--text-primary',
  textRegular: '--text-regular',
  textSecondary: '--text-secondary',
  borderColor: '--border-color',
  fontXs: '--font-xs',
  fontSm: '--font-sm',
  fontBase: '--font-base',
  fontMd: '--font-md',
  fontLg: '--font-lg',
  spaceXs: '--space-xs',
  spaceSm: '--space-sm',
  spaceBase: '--space-base',
  spaceLg: '--space-lg',
  spaceXl: '--space-xl',
  radiusSm: '--radius-sm',
  radiusBase: '--radius-base',
  radiusLg: '--radius-lg',
  shadowCard: '--shadow-card',
  shadowPop: '--shadow-pop',
}

// 允许用户在页面自定义的配置项
const customizableKeys = ['primary', 'primaryHover', 'danger', 'dangerHover', 'fontXs', 'fontSm', 'fontBase', 'fontMd', 'fontLg']

const STORAGE_KEY = 'themeConfig'

/**
 * 应用主题：将配置写入 <html> 的 CSS 变量（合并 localStorage 中的用户自定义项）
 * @returns {object} 实际生效的完整配置
 */
export function applyTheme() {
  let custom = {}
  try {
    custom = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    custom = {}
  }
  const finalCfg = Object.assign({}, theme, custom)
  const root = document.documentElement
  Object.keys(varMap).forEach((key) => {
    if (finalCfg[key] !== undefined && finalCfg[key] !== null) {
      root.style.setProperty(varMap[key], finalCfg[key])
    }
  })
  return finalCfg
}

/**
 * 保存并应用用户自定义主题（仅保存允许自定义的项）
 * @param {object} cfg 部分配置，如 { primary: '#ff0000', fontBase: '16px' }
 */
export function saveThemeConfig(cfg) {
  const safe = {}
  Object.keys(cfg).forEach((key) => {
    if (customizableKeys.indexOf(key) > -1) {
      safe[key] = cfg[key]
    }
  })
  const old = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
  const merged = Object.assign({}, old, safe)
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  return applyTheme()
}

/** 读取用户自定义主题配置 */
export function getThemeConfig() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}

/** 清除用户自定义，恢复默认主题 */
export function resetTheme() {
  window.localStorage.removeItem(STORAGE_KEY)
  return applyTheme()
}

/** 获取默认主题配置（只读） */
export function getTheme() {
  return theme
}

export default theme
